import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sparkles,
  MessageCircle,
  Search,
  CheckCircle,
  Edit3,
  X,
  Save,
  Filter,
  Trash2,
  MoreVertical,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ArrowRight,
  ArrowUp,
  Send,
  Wand2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ApiService from '../../services/ApiService';
import useBillingStore from '../../store/useBillingStore';

function AnswerOptimization() {
  const location = useLocation();

  // --- FAQ Data ---
  const [faqs, setFaqs] = useState([]);

  // --- KV Filter ---
  const [showFilterKnowledgeVaultModal, setShowFilterKnowledgeVaultModal] = useState(false);
  const [knowledgeVault, setKnowledgeVault] = useState([]);
  const [localSelectedKVIds, setLocalSelectedKVIds] = useState([]);
  const [selectedKVIds, setSelectedKVIds] = useState([]);

  // --- Search (server-side with debounce) ---
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // --- Pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // --- View ---
  const [listView, setListView] = useState(true);

  // --- Loading / Optimizing / Editing ---
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState({});
  const [editing, setEditing] = useState({});
  const [editData, setEditData] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState({});
  const [openLeft, setOpenLeft] = useState({});

  // --- Optimization Settings ---
  const [selectedOptimization, setSelectedOptimization] = useState('');
  const [customPromptText, setCustomPromptText] = useState('');

  // --- Bulk Selection ---
  const [selectedFAQIds, setSelectedFAQIds] = useState([]);
  const [bulkOptimizing, setBulkOptimizing] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ show: false, ids: [], submitting: false });

  // --- Edit Modal ---
  const [editFAQModal, setEditFAQModal] = useState({
    show: false,
    faq: null,
    question: '',
    answer: '',
    submitting: false,
  });

  // --- Expanded FAQs (list view) ---
  const [expandedFAQs, setExpandedFAQs] = useState({});

  // --- Scroll to top ---
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { billingInfo, fetchBillingInfo } = useBillingStore();

  const usageLimitReached =
    billingInfo &&
    billingInfo.limits &&
    billingInfo.usage &&
    billingInfo.usage.tokens >= billingInfo.limits.tokens;

  // ─── Optimization type definitions ────────────────────────────────────────
  const optimizationTypes = [
    { value: 'clearer',      label: 'Make Clearer',      description: 'Simplify language and improve clarity',         icon: '✨' },
    { value: 'concise',      label: 'Make Concise',       description: 'Reduce length while keeping key information',  icon: '✂️' },
    { value: 'detailed',     label: 'Add Detail',         description: 'Provide more comprehensive information',       icon: '📋' },
    { value: 'friendly',     label: 'More Friendly',      description: 'Improve tone to be more approachable',         icon: '😊' },
    { value: 'professional', label: 'More Professional',  description: 'Enhance formality and professionalism',        icon: '💼' },
    { value: 'technical',    label: 'More Technical',     description: 'Add technical depth and specificity',          icon: '🔬' },
    { value: 'custom',       label: 'Custom Prompt',      description: 'Write your own optimization instruction',      icon: '✍️' },
  ];

  // ─── Resolve actual optimization_type for API call ────────────────────────
  const resolveOptimizationType = () => {
    if (selectedOptimization === 'custom') return customPromptText.trim();
    return selectedOptimization;
  };

  // ─── Usage limit helpers ───────────────────────────────────────────────────
  const getExceededDetails = () => {
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (typeof tokensUsed === 'number' && typeof tokensLimit === 'number' && tokensUsed >= tokensLimit) {
      details.push({
        key: 'tokens',
        title: 'AI Tokens',
        used: tokensUsed,
        limit: tokensLimit,
        why: 'Your AI token quota is exhausted — FAQ optimization and AI transformations are paused. Upgrade to resume optimizations.',
      });
    }
    return details;
  };

  // ─── Data loading ──────────────────────────────────────────────────────────
  const loadFAQs = useCallback(async (page = 1, search = '', pSize = perPage) => {
    try {
      setLoading(true);

      const params = { page, per_page: pSize };
      if (search.trim()) params.search = search.trim();

      const [faqData, kvData] = await Promise.all([
        ApiService.getFAQs(params),
        ApiService.getContentList(),
      ]);

      setFaqs(faqData.faqs || []);
      setKnowledgeVault(kvData.content || []);

      const pag = faqData.pagination || {};
      setCurrentPage(pag.current_page || page);
      setTotalItems(pag.total_items || 0);
      setTotalPages(pag.total_pages || 1);

      // Clear selection when page changes
      setSelectedFAQIds([]);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, [perPage]);

  // Initial load
  useEffect(() => {
    loadFAQs(1, debouncedSearch, perPage);
    fetchBillingInfo();
  }, [fetchBillingInfo, loadFAQs, debouncedSearch, perPage]);

  // Debounce search → resets to page 1
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  // Scroll to top button
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle URL search params from Knowledge Vault navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search]);

  // ─── Filtered FAQs (KV filter is client-side only) ────────────────────────
  const getFilteredFAQs = () => {
    if (selectedKVIds.length === 0) return faqs;
    return faqs.filter(faq => selectedKVIds.includes(faq.content_id));
  };

  // ─── Pagination helpers ────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadFAQs(page, debouncedSearch, perPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    loadFAQs(1, debouncedSearch, newPerPage);
  };

  // Generate page number array with ellipsis
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem   = Math.min(currentPage * perPage, totalItems);

  // ─── Optimization handlers ─────────────────────────────────────────────────
  const handleOptimize = async (faqId) => {
    if (!selectedOptimization) {
      toast.error('Please select an optimization type');
      return;
    }
    if (selectedOptimization === 'custom' && !customPromptText.trim()) {
      toast.error('Please enter your custom prompt');
      return;
    }

    try {
      setOptimizing(prev => ({ ...prev, [faqId]: true }));
      const faq = faqs.find(f => f.faq_id === faqId);
      if (!faq) { toast.error('FAQ not found'); return; }

      const result = await ApiService.optimizeFAQ([{
        faq_id: faqId,
        optimization_type: resolveOptimizationType(),
        question: faq.question,
        answer: faq.answer,
      }]);

      if (result.success) {
        toast.success('FAQ optimized successfully!');
        const optimizedAnswer = result.result?.optimized_answers?.[0] || result.optimized_answer;
        setFaqs(prev => prev.map(f => f.faq_id === faqId ? { ...f, answer: optimizedAnswer } : f));
      } else {
        toast.error(result.message || 'Failed to optimize FAQ');
      }
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error(error.message || 'Failed to optimize FAQ');
    } finally {
      setOptimizing(prev => ({ ...prev, [faqId]: false }));
    }
  };

  const handleBulkOptimize = async () => {
    if (!selectedOptimization) {
      toast.error('Please select an optimization type');
      return;
    }
    if (selectedOptimization === 'custom' && !customPromptText.trim()) {
      toast.error('Please enter your custom prompt');
      return;
    }
    if (selectedFAQIds.length === 0) {
      toast.error('Please select at least one FAQ to optimize');
      return;
    }
    if (selectedFAQIds.length > 50) {
      toast.error('You can optimize at most 50 FAQs at once');
      return;
    }

    try {
      setBulkOptimizing(true);
      const selectedFAQs = faqs.filter(faq => selectedFAQIds.includes(faq.faq_id));
      const faqData = selectedFAQs.map(faq => ({
        faq_id: faq.faq_id,
        optimization_type: resolveOptimizationType(),
        question: faq.question,
        answer: faq.answer,
      }));

      const result = await ApiService.optimizeFAQ(faqData);

      if (result.success) {
        toast.success(`Successfully optimized ${selectedFAQIds.length} FAQ(s)!`);
        if (result.result?.faq_id && result.result?.optimized_answers) {
          setFaqs(prev => prev.map(faq => {
            const index = result.result.faq_id.indexOf(faq.faq_id);
            return index !== -1 ? { ...faq, answer: result.result.optimized_answers[index] } : faq;
          }));
        }
        setSelectedFAQIds([]);
        if (result.error?.length > 0) toast.error(`${result.error.length} FAQ(s) failed to optimize`);
      } else {
        toast.error(result.message || 'Failed to optimize FAQs');
      }
    } catch (error) {
      console.error('Bulk optimization error:', error);
      toast.error(error.message || 'Failed to optimize FAQs');
    } finally {
      setBulkOptimizing(false);
    }
  };

  // ─── Edit handlers ─────────────────────────────────────────────────────────
  const handleEdit = (faq) => {
    if (!listView) {
      setEditing(prev => ({ ...prev, [faq.faq_id]: true }));
      setEditData(prev => ({ ...prev, [faq.faq_id]: { question: faq.question, answer: faq.answer } }));
    } else {
      setEditFAQModal({ show: true, faq, question: faq.question, answer: faq.answer, submitting: false });
    }
  };

  const handleCancelEdit = (faqId) => {
    setEditing(prev => ({ ...prev, [faqId]: false }));
    setEditData(prev => ({ ...prev, [faqId]: undefined }));
  };

  const handleSaveEdit = async (faqId) => {
    try {
      const data = editData[faqId];
      if (!data) return;
      const result = await ApiService.updateFAQ({ faq_id: faqId, question: data.question, answer: data.answer });
      if (result.success) {
        toast.success('FAQ updated successfully!');
        setFaqs(prev => prev.map(f => f.faq_id === faqId ? { ...f, ...data } : f));
        setEditing(prev => ({ ...prev, [faqId]: false }));
        setEditData(prev => ({ ...prev, [faqId]: undefined }));
      } else {
        toast.error('Failed to update FAQ');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update FAQ');
    }
  };

  const handleEditModalSave = async () => {
    try {
      setEditFAQModal(prev => ({ ...prev, submitting: true }));
      const result = await ApiService.updateFAQ({
        faq_id: editFAQModal.faq.faq_id,
        question: editFAQModal.question,
        answer: editFAQModal.answer,
      });
      if (result.success) {
        toast.success('FAQ updated successfully!');
        setFaqs(prev => prev.map(f =>
          f.faq_id === editFAQModal.faq.faq_id
            ? { ...f, question: editFAQModal.question, answer: editFAQModal.answer }
            : f
        ));
        setEditFAQModal({ show: false, faq: null, question: '', answer: '', submitting: false });
      } else {
        toast.error('Failed to update FAQ');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update FAQ');
    } finally {
      setEditFAQModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleEditModalClose = () => {
    setEditFAQModal({ show: false, faq: null, question: '', answer: '', submitting: false });
  };

  // ─── Delete handlers ───────────────────────────────────────────────────────
  const handleDelete = (faqId) => {
    setConfirmDeleteModal({ show: true, ids: [faqId], submitting: false });
  };

  const handleBulkDelete = () => {
    if (selectedFAQIds.length === 0) { toast.error('Please select at least one FAQ to delete'); return; }
    setConfirmDeleteModal({ show: true, ids: [...selectedFAQIds], submitting: false });
  };

  const confirmDeletion = async () => {
    if (!confirmDeleteModal.ids?.length) return;
    try {
      setConfirmDeleteModal(prev => ({ ...prev, submitting: true }));
      const ids = confirmDeleteModal.ids;
      await ApiService.deleteFAQ(ids);
      toast.success(ids.length === 1 ? 'FAQ deleted successfully' : `Successfully deleted ${ids.length} FAQ(s)`);
      setFaqs(prev => prev.filter(faq => !ids.includes(faq.faq_id)));
      if (ids.length > 1) setSelectedFAQIds([]);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete FAQ(s)');
    } finally {
      setConfirmDeleteModal({ show: false, ids: [], submitting: false });
    }
  };

  const cancelDeletion = () => setConfirmDeleteModal({ show: false, ids: [], submitting: false });

  // ─── Selection helpers ─────────────────────────────────────────────────────
  const toggleFAQSelection = (faqId) => {
    setSelectedFAQIds(prev => prev.includes(faqId) ? prev.filter(id => id !== faqId) : [...prev, faqId]);
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredFAQs();
    if (selectedFAQIds.length === filtered.length && filtered.length > 0) {
      setSelectedFAQIds([]);
    } else {
      setSelectedFAQIds(filtered.map(f => f.faq_id));
    }
  };

  const toggleFAQExpansion = (faqId) => {
    setExpandedFAQs(prev => ({ ...prev, [faqId]: !prev[faqId] }));
  };

  // ─── Mobile menu helpers ───────────────────────────────────────────────────
  const handleMobileMenuOpen = (faqId) => {
    setMobileMenuOpen(prev => ({ ...prev, [faqId]: !prev[faqId] }));
    setTimeout(() => {
      const menu = document.getElementById(`faq-menu-${faqId}`);
      if (menu) {
        const rect = menu.getBoundingClientRect();
        setOpenLeft(prev => ({ ...prev, [faqId]: rect.right > window.innerWidth }));
      }
    }, 0);
  };

  // ─── KV filter ────────────────────────────────────────────────────────────
  const handleCheckboxToggle = (contentId) => {
    setLocalSelectedKVIds(prev =>
      prev.includes(contentId) ? prev.filter(id => id !== contentId) : [...prev, contentId]
    );
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ─── Shared markdown components ───────────────────────────────────────────
  const mdComponents = {
    h1: ({ ...props }) => <h1 className="text-lg font-bold text-gray-900 mb-3 mt-4 border-b border-gray-200 pb-1" {...props} />,
    h2: ({ ...props }) => <h2 className="text-base font-semibold text-gray-800 mb-2 mt-3" {...props} />,
    h3: ({ ...props }) => <h3 className="text-sm font-medium text-gray-800 mb-2 mt-3" {...props} />,
    h4: ({ ...props }) => <h4 className="text-sm font-medium text-gray-700 mb-1 mt-2" {...props} />,
    p:  ({ ...props }) => <p  className="text-gray-900 leading-relaxed mb-2 text-sm" {...props} />,
    ul: ({ ...props }) => <ul className="list-disc list-inside space-y-1 mb-3 ml-2" {...props} />,
    ol: ({ ...props }) => <ol className="list-decimal list-inside space-y-1 mb-3 ml-2" {...props} />,
    li: ({ ...props }) => <li className="text-gray-900 leading-relaxed text-sm" {...props} />,
    a:  ({ ...props }) => <a  className="text-blue-600 hover:text-blue-800 underline transition-colors text-sm" {...props} />,
    code: ({ inline, ...props }) =>
      inline
        ? <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded font-mono text-xs" {...props} />
        : <code className="block bg-gray-800 text-gray-100 p-3 rounded-lg font-mono text-xs overflow-x-auto" {...props} />,
    pre:        ({ ...props }) => <pre        className="bg-gray-800 rounded-lg mb-3 overflow-hidden" {...props} />,
    blockquote: ({ ...props }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-700 bg-gray-50 py-2 rounded-r-lg mb-3 text-sm" {...props} />,
    table: ({ ...props }) => <div className="overflow-x-auto mb-3"><table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} /></div>,
    th:     ({ ...props }) => <th     className="border border-gray-300 bg-gray-100 px-3 py-1.5 text-left font-semibold text-gray-800 text-xs" {...props} />,
    td:     ({ ...props }) => <td     className="border border-gray-300 px-3 py-1.5 text-gray-900 text-xs" {...props} />,
    strong: ({ ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
    em:     ({ ...props }) => <em     className="italic text-gray-800" {...props} />,
    hr:     ({ ...props }) => <hr     className="border-gray-300 my-4" {...props} />,
  };

  // ─── Confidence badge ─────────────────────────────────────────────────────
  const ConfidenceBadge = ({ confidence }) => {
    const pct = Math.round((confidence || 0.8) * 100);
    const color = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-medium text-gray-600">{pct}%</span>
      </div>
    );
  };

  // ─── Loading spinner ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[90vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black" />
      </div>
    );
  }

  const filteredFAQs = getFilteredFAQs();

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-black">Answer Optimization</h2>
          <p className="text-gray-600 mt-0.5 text-sm sm:text-base">
            Use AI to improve your FAQ answers for better customer experience
          </p>
        </div>
        {totalItems > 0 && (
          <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-xl px-4 py-2">
            <span className="text-2xl font-bold text-black">{totalItems}</span>
            <span className="text-sm text-gray-500 font-medium">Total FAQs</span>
          </div>
        )}
      </div>

      {/* ── Usage Limit Banner ─────────────────────────────────────────────── */}
      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded?.length) return null;
        return (
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                  <AlertTriangle className="h-5 w-5 text-black" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-black">Usage limit reached</h4>
                    <p className="text-sm text-gray-700/90 mt-0.5">
                      You&apos;ve reached the following monthly quota{exceeded.length > 1 ? 's' : ''}. Upgrade to continue.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-gray-800 transition-colors"
                  >
                    View billing &amp; upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-gray-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-700/80">{d.title}</span>
                          <span className="text-[11px] font-semibold text-black bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                          <div className="h-full bg-black" style={{ width: `${percent}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-gray-700/90 leading-relaxed">{d.why}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Optimization Settings ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/50 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-gray-500" />
          <h3 className="font-bold text-gray-900 text-sm">Optimization Settings</h3>
          {selectedOptimization && (
            <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">
              {optimizationTypes.find(t => t.value === selectedOptimization)?.label}
            </span>
          )}
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {optimizationTypes.map((type) => (
              <div key={type.value} className="relative">
                <input
                  type="radio"
                  id={type.value}
                  name="optimization"
                  value={type.value}
                  checked={selectedOptimization === type.value}
                  onChange={(e) => setSelectedOptimization(e.target.value)}
                  className="sr-only"
                />
                <label
                  htmlFor={type.value}
                  className={`flex flex-col gap-1 p-3.5 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md select-none ${
                    selectedOptimization === type.value
                      ? type.value === 'custom'
                        ? 'border-black bg-black text-white'
                        : 'border-black bg-gray-50'
                      : 'border-gray-200 hover:border-gray-400 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base leading-none">{type.icon}</span>
                      <h4 className={`font-bold text-sm ${selectedOptimization === type.value && type.value === 'custom' ? 'text-white' : 'text-gray-900'}`}>
                        {type.label}
                      </h4>
                    </div>
                    {selectedOptimization === type.value && (
                      <CheckCircle className={`h-4 w-4 flex-shrink-0 ${type.value === 'custom' ? 'text-white' : 'text-black'}`} />
                    )}
                  </div>
                  <p className={`text-xs leading-relaxed ${selectedOptimization === type.value && type.value === 'custom' ? 'text-gray-300' : 'text-gray-500'}`}>
                    {type.description}
                  </p>
                </label>
              </div>
            ))}
          </div>

          {/* Custom Prompt Textarea — shown only when "custom" is selected */}
          {selectedOptimization === 'custom' && (
            <div className="rounded-xl border border-black bg-gray-50 p-4 space-y-3 animate-in fade-in duration-200">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-black" />
                <label className="text-sm font-bold text-gray-900">Your Custom Prompt</label>
              </div>
              <textarea
                value={customPromptText}
                onChange={(e) => setCustomPromptText(e.target.value)}
                placeholder="e.g. Rewrite the answer in a more empathetic tone, focusing on the user's pain point and offering a clear next step."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition-all duration-200 bg-white resize-none placeholder:text-gray-400"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Filter Bar ────────────────────────────────────────────────────── */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200">
        <div className="p-2 sm:p-3">

          {/* Mobile */}
          <div className="flex flex-col gap-3 sm:hidden">
            <div className="relative w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-black/5 focus:border-black transition-all duration-200 text-sm"
              />
            </div>
            <div className="relative w-full">
              <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <button
                onClick={() => setShowFilterKnowledgeVaultModal(true)}
                className="w-full pl-10 pr-4 h-10 border border-gray-300 rounded-lg shadow-sm text-xs text-left text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center justify-between"
              >
                <span>{localSelectedKVIds.length > 0 ? `${localSelectedKVIds.length} Selected` : 'Knowledge Vault'}</span>
                {localSelectedKVIds.length > 0 && (
                  <span className="bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {localSelectedKVIds.length}
                  </span>
                )}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setSelectedKVIds(localSelectedKVIds)} className="h-10 px-3 bg-black text-white rounded-lg text-xs font-bold hover:bg-gray-800 transition-all">Apply</button>
              <button onClick={() => { setLocalSelectedKVIds([]); setSearchQuery(''); setSelectedKVIds([]); }} className="h-10 px-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-200 transition-all">Clear</button>
              <button onClick={() => setListView(prev => !prev)} className={`h-10 px-2 rounded-lg border border-gray-300 text-xs font-bold transition-all flex items-center justify-center ${listView ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                {listView ? 'Cards' : 'List'}
              </button>
            </div>
          </div>

          {/* Tablet */}
          <div className="hidden sm:flex lg:hidden flex-col gap-3 w-full">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="text" placeholder="Search FAQs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/5 focus:border-black transition-all" />
              </div>
              <div className="relative flex-1">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <button onClick={() => setShowFilterKnowledgeVaultModal(true)}
                  className="w-full pl-12 pr-4 h-[50px] border border-gray-300 rounded-xl shadow-sm text-sm text-left text-gray-700 hover:bg-gray-50 hover:border-gray-400 flex items-center justify-between transition-all">
                  <span>{localSelectedKVIds.length > 0 ? `${localSelectedKVIds.length} Knowledge Vault${localSelectedKVIds.length === 1 ? '' : 's'} Selected` : 'Filter By Knowledge Vault'}</span>
                  {localSelectedKVIds.length > 0 && <span className="bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{localSelectedKVIds.length}</span>}
                </button>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setSelectedKVIds(localSelectedKVIds)} className="h-12 px-8 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-all min-w-[120px]">Apply</button>
              <button onClick={() => { setLocalSelectedKVIds([]); setSearchQuery(''); setSelectedKVIds([]); }} className="h-12 px-8 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all min-w-[120px]">Clear</button>
              <button onClick={() => setListView(prev => !prev)} className={`h-12 px-8 rounded-xl border border-gray-300 text-sm font-medium transition-all min-w-[120px] ${listView ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                {listView ? 'Card View' : 'List View'}
              </button>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden lg:flex items-stretch gap-3 w-full">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Search FAQs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-black/5 focus:border-black transition-all text-sm" />
            </div>
            <div className="relative max-w-[220px] w-full">
              <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <button onClick={() => setShowFilterKnowledgeVaultModal(true)}
                className="w-full pl-10 pr-3 h-10 border border-gray-300 rounded-lg shadow-sm text-sm text-left text-gray-700 hover:bg-gray-50 hover:border-gray-400 flex items-center justify-between gap-1 transition-all">
                <span className="truncate">{selectedKVIds.length > 0 ? `${selectedKVIds.length} Vault${selectedKVIds.length !== 1 ? 's' : ''}` : 'Knowledge Vault'}</span>
                {selectedKVIds.length > 0 && <span className="bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold flex-shrink-0">{selectedKVIds.length}</span>}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setSelectedKVIds(localSelectedKVIds)} className="h-10 px-4 bg-black text-white rounded-lg text-sm font-bold hover:bg-gray-800 transition-all">Apply</button>
              <button onClick={() => { setLocalSelectedKVIds([]); setSearchQuery(''); setSelectedKVIds([]); }} className="h-10 px-4 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition-all">Clear</button>
              <button onClick={() => setListView(prev => !prev)} className={`h-10 px-4 rounded-lg border border-gray-300 text-sm font-bold transition-all whitespace-nowrap ${listView ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                {listView ? 'Card View' : 'List View'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── KV Filter Modal ────────────────────────────────────────────────── */}
      {showFilterKnowledgeVaultModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowFilterKnowledgeVaultModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Select Knowledge Vaults</h3>
                  <button onClick={() => setShowFilterKnowledgeVaultModal(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4 border rounded-xl bg-white shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      {localSelectedKVIds.length > 0 ? `${localSelectedKVIds.length} selected` : 'All vaults'}
                    </span>
                    <button type="button" onClick={() => setLocalSelectedKVIds([])} className="text-sm text-gray-500 hover:text-red-500 transition-colors">
                      Clear All
                    </button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto overscroll-contain">
                    {knowledgeVault?.length > 0 ? (
                      knowledgeVault.map((kv) => (
                        <label key={kv.content_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            value={kv.content_id}
                            checked={localSelectedKVIds.includes(kv.content_id)}
                            onChange={() => handleCheckboxToggle(kv.content_id)}
                            className="w-4 h-4 accent-black rounded"
                          />
                          <span className="text-sm text-gray-800 capitalize">{kv.title}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No knowledge vaults found</p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
                  <button type="button" onClick={() => setShowFilterKnowledgeVaultModal(false)} className="px-5 py-2.5 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl transition-all">Cancel</button>
                  <button type="button" onClick={() => { setSelectedKVIds([...localSelectedKVIds]); setShowFilterKnowledgeVaultModal(false); }} className="px-5 py-2.5 text-sm font-medium text-white bg-black hover:bg-gray-800 rounded-xl transition-all">
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Modal ──────────────────────────────────────────── */}
      {confirmDeleteModal.show && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Confirm Deletion</h3>
                  <button onClick={cancelDeletion} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-gray-700">
                  {confirmDeleteModal.ids.length === 1
                    ? 'Are you sure you want to delete this FAQ? This action cannot be undone.'
                    : `Are you sure you want to delete ${confirmDeleteModal.ids.length} FAQ(s)? This action cannot be undone.`}
                </p>
                <div className="flex justify-end gap-3 pt-5">
                  <button onClick={cancelDeletion} disabled={confirmDeleteModal.submitting} className="px-5 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all">Cancel</button>
                  <button onClick={confirmDeletion} disabled={confirmDeleteModal.submitting} className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all disabled:opacity-60">
                    {confirmDeleteModal.submitting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Select All + Count ────────────────────────────────────────────── */}
      {filteredFAQs.length > 0 && (
        <div className="flex items-center justify-between">
          <button
            onClick={toggleSelectAll}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 border-2 ${
              selectedFAQIds.length === filteredFAQs.length && filteredFAQs.length > 0
                ? 'bg-black text-white border-black'
                : 'bg-white text-black border-black hover:bg-gray-50'
            }`}
          >
            <CheckCircle className="h-4 w-4" />
            {selectedFAQIds.length === filteredFAQs.length && filteredFAQs.length > 0
              ? `Deselect All (${filteredFAQs.length})`
              : `Select All on This Page (${filteredFAQs.length})`}
          </button>
          {selectedFAQIds.length > 0 && (
            <span className="text-sm text-gray-600">
              {selectedFAQIds.length} FAQ{selectedFAQIds.length !== 1 ? 's' : ''} selected
            </span>
          )}
        </div>
      )}

      {/* ── Bulk Action Bar ───────────────────────────────────────────────── */}
      {selectedFAQIds.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-900">
                {selectedFAQIds.length} FAQ{selectedFAQIds.length !== 1 ? 's' : ''} selected
              </span>
              <button onClick={() => setSelectedFAQIds([])} className="text-xs text-blue-600 hover:text-blue-800 underline">
                Clear selection
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 border border-red-200 font-medium rounded-xl hover:bg-red-100 hover:border-red-300 transition-all text-sm"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>
              <button
                onClick={handleBulkOptimize}
                disabled={!selectedOptimization || bulkOptimizing || usageLimitReached}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-black text-white font-medium rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm shadow-sm"
              >
                {bulkOptimizing ? (
                  <><div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />Optimizing...</>
                ) : (
                  <><Sparkles className="h-4 w-4" />Optimize Selected</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FAQ List / Empty State ────────────────────────────────────────── */}
      {filteredFAQs.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl" />
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {totalItems === 0 ? 'No FAQs available' : 'No FAQs match your filter'}
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            {totalItems === 0
              ? 'Upload content in the Knowledge Vault to generate FAQs'
              : 'Try adjusting your Knowledge Vault filter'}
          </p>
        </div>
      ) : listView ? (

        /* ═══════════════ LIST VIEW ═══════════════════════════════════════════ */
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full">
          <div className="divide-y divide-gray-100">
            {filteredFAQs.map((faq) => (
              <div key={faq.faq_id}>
                {/* Collapsed row */}
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedFAQIds.includes(faq.faq_id)}
                    onChange={(e) => { e.stopPropagation(); toggleFAQSelection(faq.faq_id); }}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-2 focus:ring-black/5 cursor-pointer flex-shrink-0"
                  />

                  {/* Expand toggle + content */}
                  <button
                    onClick={() => toggleFAQExpansion(faq.faq_id)}
                    className="flex-1 text-left flex items-center gap-3 min-w-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-black truncate pr-2">
                        {faq.question}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <ConfidenceBadge confidence={faq.confidence} />
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-400">
                          {new Date(faq.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${expandedFAQs[faq.faq_id] ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {/* Expanded section */}
                {expandedFAQs[faq.faq_id] && (
                  <div className="px-4 pb-4 bg-gray-50/50">
                    {/* Answer */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Answer</h4>
                      <div className="prose prose-gray max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                          {faq.answer}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(faq.faq_id); }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(faq); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (usageLimitReached) {
                            toast.error('You have exceeded your token usage limit. Please upgrade.');
                          } else {
                            handleOptimize(faq.faq_id);
                          }
                        }}
                        disabled={!selectedOptimization || optimizing[faq.faq_id]}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-all"
                      >
                        {optimizing[faq.faq_id] ? (
                          <><div className="animate-spin h-3 w-3 border-2 border-white/30 border-t-white rounded-full" />Optimizing...</>
                        ) : (
                          <><Sparkles className="h-3.5 w-3.5" />Optimize</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      ) : (

        /* ═══════════════ CARD VIEW ═══════════════════════════════════════════ */
        <div className="grid grid-cols-1 gap-4">
          {filteredFAQs.map((faq) => (
            <div
              key={faq.faq_id}
              className="group bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-300 relative"
            >
              <div className="p-5">
                {/* Top row: checkbox + question + actions */}
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="pt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={selectedFAQIds.includes(faq.faq_id)}
                      onChange={(e) => { e.stopPropagation(); toggleFAQSelection(faq.faq_id); }}
                      className="w-4 h-4 text-black border-gray-300 rounded focus:ring-2 focus:ring-black/5 cursor-pointer"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {editing[faq.faq_id] ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Question</label>
                          <input
                            type="text"
                            value={editData[faq.faq_id]?.question || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [faq.faq_id]: { ...prev[faq.faq_id], question: e.target.value } }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black/10 focus:border-black transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Answer</label>
                          <textarea
                            value={editData[faq.faq_id]?.answer || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [faq.faq_id]: { ...prev[faq.faq_id], answer: e.target.value } }))}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-black/10 focus:border-black transition-all resize-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleSaveEdit(faq.faq_id)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-all">
                            <Save className="h-3.5 w-3.5" />Save
                          </button>
                          <button onClick={() => handleCancelEdit(faq.faq_id)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all">
                            <X className="h-3.5 w-3.5" />Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Question */}
                        <h3 className="text-base font-bold text-gray-900 group-hover:text-black transition-colors leading-snug mb-2 pr-8 sm:pr-0">
                          {faq.question}
                        </h3>
                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 font-medium">Confidence:</span>
                            <ConfidenceBadge confidence={faq.confidence} />
                          </div>
                          <span className="text-gray-300">·</span>
                          <span className="text-xs text-gray-400">
                            Created: {new Date(faq.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Desktop action buttons */}
                  {!editing[faq.faq_id] && (
                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleDelete(faq.faq_id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleEdit(faq)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all">
                        <Edit3 className="h-3.5 w-3.5" />Edit
                      </button>
                      <button
                        onClick={() => { if (usageLimitReached) { toast.error('Token limit reached. Please upgrade.'); } else { handleOptimize(faq.faq_id); } }}
                        disabled={!selectedOptimization || optimizing[faq.faq_id]}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-all shadow-sm"
                      >
                        {optimizing[faq.faq_id] ? (
                          <><div className="animate-spin h-3 w-3 border-2 border-white/30 border-t-white rounded-full" />Optimizing...</>
                        ) : (
                          <><Sparkles className="h-3.5 w-3.5" />Optimize</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Mobile three-dot menu */}
                  {!editing[faq.faq_id] && (
                    <div className="sm:hidden relative">
                      <button onClick={() => handleMobileMenuOpen(faq.faq_id)} className="p-2 rounded-full hover:bg-gray-100 text-gray-500">
                        <MoreVertical className="h-5 w-5" />
                      </button>
                      {mobileMenuOpen[faq.faq_id] && (
                        <div
                          id={`faq-menu-${faq.faq_id}`}
                          className={`absolute mt-1 w-36 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden ${openLeft[faq.faq_id] ? 'right-0' : 'left-0'}`}
                        >
                          <button onClick={() => { setMobileMenuOpen({}); handleEdit(faq); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                            <Edit3 className="h-4 w-4" />Edit
                          </button>
                          <button onClick={() => { setMobileMenuOpen({}); handleOptimize(faq.faq_id); }} disabled={!selectedOptimization || optimizing[faq.faq_id]} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                            <Sparkles className="h-4 w-4" />Optimize
                          </button>
                          <button onClick={() => { setMobileMenuOpen({}); handleDelete(faq.faq_id); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Answer */}
                {!editing[faq.faq_id] && (
                  <div className="mt-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Answer</h4>
                    <div className="prose prose-gray max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                        {faq.answer}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination Bar ────────────────────────────────────────────────── */}
      {totalPages > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Summary + per-page */}
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span>
                {totalItems === 0 ? '0 FAQs' : `Showing ${startItem}–${endItem} of ${totalItems} FAQs`}
              </span>
              <span className="text-gray-300">|</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Per page:</span>
                <select
                  value={perPage}
                  onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black bg-white"
                >
                  {[10, 25, 50].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Page controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* Previous */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                {/* Page numbers */}
                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-xs select-none">…</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-8 h-8 text-xs font-medium rounded-lg transition-all ${
                        currentPage === page
                          ? 'bg-black text-white shadow-sm'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                {/* Next */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── Edit FAQ Modal ────────────────────────────────────────────────── */}
      {editFAQModal.show && editFAQModal.faq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit FAQ</h2>
                <button onClick={handleEditModalClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-2">Question</label>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <textarea
                    value={editFAQModal.question}
                    onChange={(e) => setEditFAQModal(prev => ({ ...prev, question: e.target.value }))}
                    className="w-full bg-transparent resize-none border-none focus:outline-none text-gray-800 text-sm"
                    rows="3"
                    placeholder="Enter question..."
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-2">Answer</label>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <textarea
                    value={editFAQModal.answer}
                    onChange={(e) => setEditFAQModal(prev => ({ ...prev, answer: e.target.value }))}
                    className="w-full bg-transparent resize-none border-none focus:outline-none text-gray-800 text-sm"
                    rows="8"
                    placeholder="Enter answer..."
                  />
                </div>
              </div>
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Knowledge Vault ID:</span>
                  <p className="text-sm text-gray-800 mt-1">{editFAQModal.faq.knowledge_vault_id || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Created:</span>
                  <p className="text-sm text-gray-800 mt-1">
                    {editFAQModal.faq.created_at ? new Date(editFAQModal.faq.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button onClick={handleEditModalClose} className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all font-medium text-sm">Cancel</button>
                <button
                  onClick={handleEditModalSave}
                  disabled={editFAQModal.submitting || !editFAQModal.question.trim() || !editFAQModal.answer.trim()}
                  className="px-6 py-2.5 bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all font-medium flex items-center gap-2 text-sm"
                >
                  {editFAQModal.submitting ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
                  ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Scroll to Top ─────────────────────────────────────────────────── */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 p-3 bg-black hover:bg-gray-800 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

export default AnswerOptimization;
